import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import { resendVerification as resendVerificationApi } from '../services/authService';
import { analytics } from '../utils/analytics';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const isTokenExpired = (token) => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Consider expired if less than 60 seconds remaining
        return payload.exp * 1000 < Date.now() + 60000;
      } catch {
        return true;
      }
    };

    const refreshAccessToken = async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken || isTokenExpired(refreshToken)) return false;

      try {
        const response = await api.post('/auth/refresh', { refreshToken });
        if (response.data.success) {
          localStorage.setItem('accessToken', response.data.data.accessToken);
          return true;
        }
      } catch {
        return false;
      }
      return false;
    };

    const checkAuth = async () => {
      let token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }

      // Proactively refresh if access token is expired/near-expiry
      if (isTokenExpired(token)) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setLoading(false);
          return;
        }
      }

      try {
        const response = await api.get('/auth/me');
        if (response.data.success) {
          setUser(response.data.data.user);
        }
      } catch (err) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const register = useCallback(async (email, password, name, referralCode = '') => {
    setError(null);
    try {
      const payload = { email, password, name };
      if (referralCode) {
        payload.referralCode = referralCode;
      }
      const response = await api.post('/auth/register', payload);

      if (response.data.success) {
        const { user: userData, accessToken, refreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        setUser(userData);
        analytics.identify(userData._id || userData.id, { email: userData.email, name: userData.name });
        analytics.track('user_signed_up', { referral: !!referralCode });
        return { success: true };
      }

      return { success: false, error: response.data.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Registration failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await api.post('/auth/login', { email, password });

      if (response.data.success) {
        const { user: userData, accessToken, refreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        setUser(userData);
        analytics.identify(userData._id || userData.id, { email: userData.email, name: userData.name });
        analytics.track('user_logged_in');
        return { success: true };
      }

      return { success: false, error: response.data.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      await api.post('/auth/logout', { refreshToken });
    } catch (err) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      analytics.reset();
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data.success) {
        setUser(response.data.data.user);
      }
    } catch (err) {
      // Handle error silently
    }
  }, []);

  const resendVerification = useCallback(async () => {
    const result = await resendVerificationApi();
    return result;
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isEmailVerified: user?.isEmailVerified ?? false,
    register,
    login,
    logout,
    updateUser,
    refreshUser,
    resendVerification,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
